import { FC, useEffect, useState } from "react";
import { default as couponService } from "@/services/coupons";

import { 
  TextField, FormHelperText, Grid,
  Alert
} from "@mui/material";
import { Button } from "@gisce/oficina-virtual-components";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Controller, useFormContext } from "react-hook-form";
import { truncate } from "fs";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";

interface CouponFieldProps {
  fieldName?: string;
  outterIsSubmitting: boolean;
}

const CouponField: FC<CouponFieldProps> = ({
  fieldName = "promotionalCode",
  outterIsSubmitting
}) => {
  const Settings = useSettingsContext();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [textFieldValue, setTextFieldValue] = useState('');
  const [isSubmitting, setIsSubmitting] =  useState(outterIsSubmitting);
  const [couponHasError, setCouponHasError] = useState(false);
  const [searchParamsErrorCoupons, setSearchParamsErrorCoupons] = useState<string[]>([]);

  const { control, getValues, setValue } = useFormContext(); 

  useEffect(() => {

    const checkPromocodes = async (promocodes: string[]) => {
      let invalidPromocodes: string[] = [];
      for (const code of promocodes) {
        const valid = await isCouponValid(code);
        if (valid) {
          addCorrectCoupon(code);
        } else {
          invalidPromocodes.push(code);
        }
      }
      setSearchParamsErrorCoupons(invalidPromocodes);
    }

    const promocodeQS = searchParams.get("promocode");
    searchParams.delete("promocode");
    setSearchParams(searchParams);

    let promocodes = []
    if (promocodeQS?.includes(",")) {
      promocodes = promocodeQS.split(",");
      if (Settings.features.promotionalCode?.multiple) {
        // Remove duplicates
        promocodes = [...new Set(promocodes)];
      } else {
        promocodes = promocodes.slice(0,1);
      }
    } else if (promocodeQS) {
      promocodes.push(promocodeQS);
    }

    checkPromocodes(promocodes);
    
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Inner component
    setTextFieldValue(event.target.value);
    setCouponHasError(false);
  }

  const isCouponValid = async (promocode: string) => {
    let isValid = false;
    setIsSubmitting(true);
    await couponService.validateCoupon(promocode)
    .then( res => {
      isValid = res.valid;
    })
    setIsSubmitting(false);
    return isValid;
  }

  const addCorrectCoupon = (promocode: string) => {
    if (Settings.features.promotionalCode?.multiple) {
      setValue(fieldName, [...getValues(fieldName), promocode]);
    } else {
      setValue(fieldName, [promocode]);
    }
    setTextFieldValue('');
  }

  const applyChange = () => {
    if(textFieldValue) {

      // Si el cupó ja ha estat activat
      if (getValues(fieldName).includes(textFieldValue)) {
        return;
      }

      isCouponValid(textFieldValue).then(valid => {
        if (valid) {
          addCorrectCoupon(textFieldValue)
        } else {
          setCouponHasError(true);
        }
      })
    }
  }

  const test = ['codi1', 'codi2'];

  return (
    <>
      <Grid container spacing={1} 
        sx={{width: {md: "50%", xs: "100%"}, mb: "10px"}}
      >
        <Grid item xs={10}>
          <Controller 
            control={control}
            name={fieldName}
            render={() => 
              <>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={textFieldValue}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  error={!isSubmitting && couponHasError}
                />
      
                {!isSubmitting && couponHasError && 
                  <FormHelperText error={true}>
                    {t("common:text.contractation_promotional_invalid")}
                  </FormHelperText>
                }
              </>
            }
          />
        </Grid>

        <Grid item xs={2}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            loading={isSubmitting}
            onClick={() => applyChange()}
          >
            {t("common:text.contractation_promotional_apply")}
          </Button>
        </Grid>

        {!!searchParamsErrorCoupons.length && <>
          <Grid item xs={10}>
            <Alert 
              variant="outlined"
              severity="error"
              onClose={() => {setSearchParamsErrorCoupons([])}}
            >
              {t("common:text.contractation_promotional_which_invalid", {
                count: searchParamsErrorCoupons.length, 
                promocode: searchParamsErrorCoupons.join(", ")
              })}
            </Alert>
          </Grid>
        </>}
      </Grid>
    </>
  )
};

export default CouponField;